import React, { useCallback, useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import moment from 'moment';
import { Plus, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'vendor/heroui';
import { Container } from 'components/HeroSemantic';
import { AddPasskeyModal, PasskeyPlatformIcon, RecoveryCodesModal, ThemeHeader } from 'components';
import { display } from 'state/modules/dialog';
import usePasskey from 'hooks/Auth/usePasskey';
import canManagePasskeys from 'utils/auth/canManagePasskeys';
import {markPasskeyRegistered, passkeyList, passkeyListForOwner, passkeyRename, recoveryGenerate, recoveryStatus, recoveryStatusForOwner} from 'state/modules/auth';
import passkeyButtonClassName, { passkeyDangerButtonClassName } from 'components/Auth/passkeyButtonClassName';
import passkeyErrorMessageId from 'utils/auth/passkeyErrorMessageId';
import { MAX_DEVICE_NAME_LENGTH } from 'components/Auth/AddPasskeyModal';

// FTR-7: an owner can register at most this many passkeys at once
const MAX_PASSKEYS = 2;

const Passkeys = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { register, remove, isBusy, error } = usePasskey();
  const usedPasskey = useSelector(state => state.auth.usedPasskey);
  // OWN-224 step-up: defense-in-depth, independent of the RenderRoute guard
  // that's supposed to force /verify before this page ever renders - if that
  // guard is ever bypassed, actions stay disabled rather than trusting the
  // page was reached legitimately. Session-only; see state/modules/auth.
  const sessionVerified = useSelector(state => state.auth.sessionVerified);
  // OWN-229: state.auth.admin stays populated for the whole admin session,
  // whether or not an owner has been selected yet (see setUser/SelectOwnerId -
  // it only ever overwrites state.auth.user, never clears admin). So "admin
  // is impersonating a specific owner" is admin AND user both set, not admin
  // alone - an admin with no owner selected is just managing their own
  // account and gets full normal passkey management below.
  const admin = useSelector(state => state.auth.admin);
  const impersonatedOwner = useSelector(state => state.auth.user);
  const isAdmin = Boolean(admin);
  const isImpersonating = isAdmin && Boolean(impersonatedOwner);
  const tenantId = useSelector(state => state.tenant.id);
  // Shared with App.js's global low-codes banner via redux (see auth reducer's
  // RECOVERY_STATUS_SUCCESS/RECOVERY_GENERATE_SUCCESS cases) - how many codes
  // from the current batch are still unused. A fresh batch can't be generated
  // on demand until this reaches 0. This is the ADMIN's own count - while
  // impersonating, the relevant count is the impersonated owner's, kept in
  // local state below instead (impersonatedCodesRemaining) so it never
  // clobbers the admin's own shared banner state.
  const codesRemaining = useSelector(state => state.auth.recoveryCodesRemaining);
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  // OWN-229: the impersonated owner's recovery-code count, display-only - no
  // generate action exists for a different owner's codes (see canManage),
  // so this deliberately never touches the shared recoveryCodesRemaining
  // redux field above, which reflects the admin's OWN account instead.
  const [impersonatedCodesRemaining, setImpersonatedCodesRemaining] = useState(null);
  // Codes are shown exactly once, right after a batch is issued (either
  // automatically on the first passkey, or via the manual button below).
  const [revealedCodes, setRevealedCodes] = useState(null);
  // OWN-219: name is captured before the ceremony runs, rather than via a
  // follow-up rename - this is purely our own app-side label though; WebAuthn
  // gives the relying party no way to set the name the browser/OS shows the
  // user for the credential itself.
  const [showAddModal, setShowAddModal] = useState(false);
  // Disables the Generate Backup Codes button for the duration of the request -
  // without this, a rapid double-click (or two open tabs) fires two concurrent
  // requests before the first one's response updates codesRemaining, which in
  // testing produced 16 active codes instead of 8. Belt-and-suspenders alongside
  // owner-api's own generateIfNoneActive guard, which is the actual fix.
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const hasEnrolledPasskey = passkeys.length > 0;
  // Once enrolled, adding another passkey, renaming/revoking an existing one,
  // or generating a new recovery code all require EITHER having signed in
  // with a passkey this session OR a step-up verification having succeeded
  // (OWN-224) - also enforced server-side (owner-api's assertCanManagePasskeys
  // rejects it regardless: !hasEnrolledPasskey || usedPasskey || sessionVerified);
  // hiding these actions here is purely to avoid presenting controls we
  // already know will be rejected. Bootstrapping the very first passkey (no
  // enrolled passkey yet) is exempt from both usedPasskey and sessionVerified -
  // canManagePasskeys' own !hasEnrolledPasskey clause already covers that.
  // OWN-229: register/rename/revoke are all self-scoped server-side
  // (owner-auth.controller.ts resolves ownerId from the authenticated
  // account's own JWT, no ownerId param) - there is no backend support for
  // managing a DIFFERENT owner's passkeys, only viewing them
  // (passkeyListForOwner, admin-only). While impersonating,
  // usedPasskey/sessionVerified above still reflect the admin's OWN login -
  // so without the isImpersonating check, canManage could read as true and
  // let an admin silently register/revoke a passkey against their OWN
  // account while believing they're managing the owner's.
  const canManage = !isImpersonating && (canManagePasskeys(hasEnrolledPasskey, usedPasskey) || sessionVerified);
  const atPasskeyLimit = passkeys.length >= MAX_PASSKEYS;

  // OWN-229 (CodeRabbit): if the admin switches the impersonated owner while
  // a fetch is still in flight, that stale response could resolve after a
  // newer one and overwrite it - showing stale passkeys/recovery data. An
  // owner-id comparison alone isn't enough to catch this: switching 7 -> 8
  // -> 7 produces the SAME owner id for both the first (stale) and third
  // (current) refresh() calls, so a stale-id check can't tell them apart.
  // Each refresh() call instead captures a monotonically incrementing
  // token; the result is only committed if that exact call is still the
  // most recent one when its response comes back.
  const activeRefreshIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const refreshId = activeRefreshIdRef.current + 1;
    activeRefreshIdRef.current = refreshId;
    setPasskeys([]);
    setImpersonatedCodesRemaining(null);
    setLoading(true);
    try {
      // OWN-229: while impersonating, fetch the OWNER's passkeys and
      // recovery-code status via the admin-only read endpoints instead of
      // the self-scoped ones - both display-only, neither has a generate/
      // register/rename/revoke counterpart for a DIFFERENT owner.
      const [list, recovery] = isImpersonating ?
        await Promise.all([dispatch(passkeyListForOwner(impersonatedOwner.id)), dispatch(recoveryStatusForOwner(impersonatedOwner.id))])
        : await Promise.all([dispatch(passkeyList()), dispatch(recoveryStatus())]);
      if (activeRefreshIdRef.current !== refreshId) {
        // A newer refresh() call has since started - this response is
        // stale, discard it rather than committing it.
        return;
      }
      setPasskeys(Array.isArray(list) ? list : []);
      if (isImpersonating) {
        setImpersonatedCodesRemaining(recovery?.remaining ?? null);
      }
    }
    catch (err) {
      if (activeRefreshIdRef.current !== refreshId) {
        return;
      }
      // Otherwise a failed fetch leaves passkeys/codesRemaining at their
      // initial values, which reads as "you have no passkeys yet" instead
      // of surfacing that the load actually failed.
      dispatch(display(formatMessage({ id: 'notifications.title.error' }), err.message, 'error'));
    }
    finally {
      if (activeRefreshIdRef.current === refreshId) {
        setLoading(false);
      }
    }
  }, [dispatch, formatMessage, isImpersonating, impersonatedOwner]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (error) {
      dispatch(display(formatMessage({ id: 'notifications.title.error' }), formatMessage({ id: passkeyErrorMessageId(error) }), 'error'));
    }
  }, [error, dispatch, formatMessage]);

  // Only the add-passkey flow needs a reload on dismiss (the passkeys table
  // below has no other way to learn about the new row); generating a fresh
  // recovery-code batch doesn't touch that table at all, and codesRemaining
  // already updates reactively via redux (RECOVERY_GENERATE_SUCCESS) - a
  // reload there was actively harmful, wiping usedPasskey along with it.
  const reloadOnCodesDismissedRef = useRef(false);

  const handleAdd = async deviceName => {
    const { added, recoveryCodes } = await register(deviceName);
    if (added) {
      // OWN-232 passkey review: dispatched alongside the reload below (not
      // instead of it) so the global hasPasskey/passkeyRegistrationRequired
      // flags (nudge/mandatory-modal logic in App.js) are correct even in the
      // instant before the reload actually takes effect, and so they stay
      // correct if the reload is ever removed here without this line also
      // being removed.
      dispatch(markPasskeyRegistered());
      // Present only on the account's first passkey - owner-api includes the
      // freshly issued batch in the register/verify response exactly once,
      // and it's never retrievable again, so the reload below waits until
      // the owner has confirmed they've saved it (handleCodesDismissed).
      if (recoveryCodes) {
        reloadOnCodesDismissedRef.current = true;
        setRevealedCodes(recoveryCodes);
      }
      else {
        // A full reload (rather than just refetching the list into state)
        // guarantees the table reflects the new passkey with no edge cases.
        window.location.reload();
      }
    }
  };

  const handleCodesDismissed = () => {
    setRevealedCodes(null);
    if (reloadOnCodesDismissedRef.current) {
      window.location.reload();
    }
  };

  // Continuing dismisses the naming step immediately and hands off to the
  // browser's native WebAuthn prompt - if the owner cancels that, they simply
  // land back on this page, same as if they'd never opened the modal.
  const handleAddContinue = deviceName => {
    setShowAddModal(false);
    handleAdd(deviceName);
  };

  const handleRename = async id => {
    if (!canManage) {
      return;
    }
    const deviceName = window.prompt(formatMessage({ id: 'page.passkeys.prompt.rename', defaultMessage: 'Name this passkey' }));
    if (!deviceName) {
      return;
    }
    try {
      await dispatch(passkeyRename(id, deviceName.trim().slice(0, MAX_DEVICE_NAME_LENGTH)));
      await refresh();
    }
    catch (err) {
      dispatch(display(formatMessage({ id: 'notifications.title.error' }), err.message, 'error'));
    }
  };

  const handleRevoke = async passkey => {
    if (!canManage) {
      return;
    }
    // OWN-222's exact confirmation copy, plus a soft warning appended for the
    // last-remaining-passkey case (password login is still available either way).
    const message = passkeys.length <= 1 ?
      formatMessage({
        id: 'page.passkeys.confirm.revokeLast',
        defaultMessage: 'You will no longer be able to use this passkey to sign in to Owners Portal. This is your only passkey - you can still sign in with your password.',
      })
      : formatMessage({
        id: 'page.passkeys.confirm.revoke',
        defaultMessage: 'You will no longer be able to use this passkey to sign in to Owners Portal.',
      });
    if (window.confirm(message)) {
      try {
        // Past the FTR-3 deadline, revoking the last passkey flips
        // passkeyRegistrationRequired back to true in redux (PASSKEY_REVOKE_SUCCESS)
        // - the global mandatory modal (App.js) reacts to that everywhere except this
        // page, where the empty-state "Add Passkey" button below already covers it.
        await remove(passkey.id);
        await refresh();
      }
      catch {
        // A failed revoke already surfaces via errorMiddleware's global error dialog
        // (the PASSKEY_REVOKE_FAIL action carries action.error) - this just prevents
        // an unhandled promise rejection and skips refresh() on failure.
      }
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    if (!canManage || codesRemaining !== 0 || generatingCodes) {
      return;
    }
    setGeneratingCodes(true);
    try {
      const result = await dispatch(recoveryGenerate(tenantId));
      if (Array.isArray(result?.codes)) {
        // No refresh()/reload needed - codesRemaining already updates reactively
        // via redux (see auth reducer's RECOVERY_GENERATE_SUCCESS case), and this
        // doesn't touch the passkeys list at all.
        reloadOnCodesDismissedRef.current = false;
        setRevealedCodes(result.codes);
      }
    }
    catch (err) {
      dispatch(display(formatMessage({ id: 'notifications.title.error' }), err.message, 'error'));
    }
    finally {
      setGeneratingCodes(false);
    }
  };

  const canGenerateRecoveryCodes = canManage && codesRemaining === 0;
  // OWN-229: while impersonating, show the impersonated owner's recovery-code
  // count (read-only, no generate action) instead of the admin's own -
  // canManage is already false then, so this can't be folded into it above.
  const showRecoveryInfo = isImpersonating ? hasEnrolledPasskey && impersonatedCodesRemaining !== null : canManage && hasEnrolledPasskey;
  const displayedCodesRemaining = isImpersonating ? impersonatedCodesRemaining : codesRemaining;

  return (
    <Container>
      <Helmet title={formatMessage({ id: 'page.passkeys.title', defaultMessage: 'Security & Login' })} />
      <ThemeHeader as="h1" titleId="page.passkeys.title" defaultMessage="Security & Login" />

      <p className="mb-4 text-sm text-[var(--owner-text)]">
        <FormattedMessage
          id="page.passkeys.description"
          defaultMessage="Passkeys let you sign in without a password using your device biometrics or a security key."
        />
      </p>

      {/* isImpersonating takes priority: !canManage is also true then (see
          canManage above), but the reason is impersonation itself, not an
          unverified session - showing the sign-in message would incorrectly
          suggest the admin's OWN passkey could fix this. */}
      {!loading && !canManage && isImpersonating && (
        <p className="mb-4 text-sm text-[var(--owner-text-muted)]">
          <FormattedMessage
            id="page.passkeys.adminImpersonating"
            defaultMessage="Viewing this owner's passkeys as an administrator. Passkey management is disabled while impersonating."
          />
        </p>
      )}

      {!loading && !canManage && !isImpersonating && (
        <p className="mb-4 text-sm text-[var(--owner-text-muted)]">
          <FormattedMessage
            id="page.passkeys.signInRequired"
            defaultMessage="Sign in with one of your existing passkeys to add, rename, or remove passkeys, or to generate new recovery codes."
          />
        </p>
      )}

      {/* Recovery codes are only ever issued alongside the first passkey - an
          owner who has never created one has no recovery-code concept yet, so
          this must not show (codesRemaining === 0 is otherwise indistinguishable
          between "never had any" and "used them all up"). Shown read-only
          (no Generate button) while impersonating - see showRecoveryInfo. */}
      {!loading && showRecoveryInfo && (
        <div
          className={`mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4 ${
            displayedCodesRemaining === 0 ?
              'border-[var(--owner-warning)] bg-[color-mix(in_srgb,var(--owner-warning)_12%,var(--owner-surface))]'
              : 'border-[var(--owner-border)] bg-[var(--owner-surface)]'}`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                displayedCodesRemaining === 0 ?
                  'bg-[color-mix(in_srgb,var(--owner-warning)_20%,transparent)]'
                  : 'bg-[color-mix(in_srgb,#16a34a_15%,transparent)]'}`}
            >
              {displayedCodesRemaining === 0 ?
                <TriangleAlert size={20} className="text-[var(--owner-warning)]" aria-hidden />
                : <ShieldCheck size={20} className="text-green-600" aria-hidden />}
            </span>
            {displayedCodesRemaining > 0 ?
              (
                <p className="text-sm font-semibold text-[var(--owner-text)]">
                  {canManage ? (
                    <FormattedMessage
                      id="page.passkeys.recovery.remaining"
                      defaultMessage="<b>{remaining, plural, one {# recovery code} other {# recovery codes}}</b> remaining. New codes can be generated once these run out."
                      values={{ remaining: displayedCodesRemaining, b: chunks => chunks }}
                    />
                  ) : (
                    <FormattedMessage
                      id="page.passkeys.recovery.remaining.readOnly"
                      defaultMessage="<b>{remaining, plural, one {# recovery code} other {# recovery codes}}</b> remaining."
                      values={{ remaining: displayedCodesRemaining, b: chunks => chunks }}
                    />
                  )}
                </p>
              )
              : (
                <p className="text-sm font-semibold text-[var(--owner-warning)]">
                  {canManage ? (
                    <FormattedMessage
                      id="page.passkeys.recovery.none"
                      defaultMessage="You have no recovery codes left, so you won't be able to recover access if you lose your passkey device. Generate a new batch below."
                    />
                  ) : (
                    <FormattedMessage
                      id="page.passkeys.recovery.none.readOnly"
                      defaultMessage="This owner has no recovery codes left, so they won't be able to recover access if they lose their passkey device."
                    />
                  )}
                </p>
              )}
          </div>
          {canManage && (
            <Button type="button" isDisabled={!canGenerateRecoveryCodes || generatingCodes} className={passkeyButtonClassName} onPress={handleGenerateRecoveryCodes}>
              <FormattedMessage id="page.passkeys.action.generateRecovery" defaultMessage="Generate Backup Codes" />
            </Button>
          )}
        </div>
      )}

      {!loading && canManage && atPasskeyLimit && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-[var(--owner-warning)] bg-[color-mix(in_srgb,var(--owner-warning)_12%,var(--owner-surface))] p-3 text-sm font-medium text-[var(--owner-warning)]">
          <TriangleAlert size={16} className="shrink-0" aria-hidden />
          <FormattedMessage
            id="page.passkeys.limitReached"
            defaultMessage="You've reached the limit of {max} passkeys. Remove one to add another."
            values={{ max: MAX_PASSKEYS }}
          />
        </div>
      )}

      {/* The list reflects whichever account refresh() fetched above - the
          signed-in account's own passkeys normally, or the impersonated
          owner's (via passkeyListForOwner) while an admin is impersonating -
          rename/remove controls below stay gated on canManage either way. */}
      {!loading && isImpersonating && (
        <h2 className="mb-3 text-sm font-bold text-[var(--owner-text)]">
          <FormattedMessage id="page.passkeys.sectionTitle.owner" defaultMessage="Owner's passkeys" />
        </h2>
      )}

      {!loading && !isImpersonating && (
        <h2 className="mb-3 text-sm font-bold text-[var(--owner-text)]">
          <FormattedMessage id="page.passkeys.sectionTitle" defaultMessage="Your passkeys" />
        </h2>
      )}

      {loading ? (
        <p><FormattedMessage id="general.loading" defaultMessage="Loading..." /></p>
      ) : (
        <ul className="mb-4 flex flex-col gap-3">
          {passkeys.length === 0 && (
            <li className="text-sm text-[var(--owner-text-muted)]">
              <FormattedMessage id="page.passkeys.empty" defaultMessage="You have no passkeys yet." />
            </li>
          )}
          {passkeys.map(passkey => (
            <li key={passkey.id} className="flex flex-col items-start gap-3 rounded-xl border border-[var(--owner-border)] bg-[var(--owner-surface)] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex w-full items-center gap-3 sm:w-auto sm:min-w-0 sm:flex-1">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--owner-surface-muted)]">
                  <PasskeyPlatformIcon aaguid={passkey.aaguid} backedUp={passkey.backedUp} size={18} className="text-[var(--owner-text-muted)]" />
                </span>
                <div className="min-w-0 flex-1 break-words">
                  <p className="font-semibold text-[var(--owner-text)]">
                    {passkey.deviceName || formatMessage({ id: 'page.passkeys.unnamed', defaultMessage: 'Unnamed passkey' })}
                    <span className="ml-2 text-xs font-normal text-[var(--owner-text-muted)]">
                      {passkey.backedUp ?
                        formatMessage({ id: 'page.passkeys.synced', defaultMessage: 'Synced across your devices' })
                        : formatMessage({ id: 'page.passkeys.deviceBound', defaultMessage: 'This device only' })}
                    </span>
                  </p>
                  <p className="text-xs text-[var(--owner-text-muted)]">
                    {passkey.createdAt && (
                      <FormattedMessage
                        id="page.passkeys.dateAdded"
                        defaultMessage="Added {date}"
                        values={{ date: moment(passkey.createdAt).format('l') }}
                      />
                    )}
                    {passkey.createdAt && ' · '}
                    {passkey.lastUsedAt ?
                      (
                        <FormattedMessage
                          id="page.passkeys.lastUsed"
                          defaultMessage="Last used {date}"
                          values={{ date: moment(passkey.lastUsedAt).format('l') }}
                        />
                      ) : (
                        <FormattedMessage id="page.passkeys.neverUsed" defaultMessage="Never used" />
                      )}
                  </p>
                </div>
              </div>
              {canManage && (
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" type="button" className={passkeyButtonClassName} onPress={() => handleRename(passkey.id)}>
                    <FormattedMessage id="general.rename" defaultMessage="Rename" />
                  </Button>
                  <Button size="sm" type="button" className={passkeyDangerButtonClassName} onPress={() => handleRevoke(passkey)}>
                    <FormattedMessage id="general.remove" defaultMessage="Remove" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!loading && canManage && (
        <Button
          type="button"
          isDisabled={isBusy || atPasskeyLimit}
          className="inline-flex items-center gap-2 self-start rounded-full border border-[var(--owner-border-strong)] bg-transparent px-4 py-2 font-normal text-[var(--owner-text)] shadow-none hover:bg-[var(--owner-surface-muted)] disabled:cursor-not-allowed disabled:opacity-50"
          onPress={() => setShowAddModal(true)}
        >
          <Plus size={16} aria-hidden />
          <FormattedMessage id="page.passkeys.action.add" defaultMessage="Add Passkey" />
        </Button>
      )}

      <AddPasskeyModal
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        onContinue={handleAddContinue}
        isBusy={isBusy}
      />

      <RecoveryCodesModal codes={revealedCodes} onDone={handleCodesDismissed} />
    </Container>
  );
};

export default Passkeys;
